import { useCallback, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';
import { getFirstContactEligibility } from '../api/messageApi';
import { writePendingIntent } from '../utils/pendingIntentStorage';
import { PreChatIntentModal } from '../components/PreChatIntentModal';

export type UseSpMessageLaunchParams = {
  /** service_providers.id — every SP-targeting CTA emits this, never a user id (spec §3.1). */
  serviceProviderId: string;
  /** Display name shown in the pre-chat modal's context panel. */
  providerName: string;
  sourceSurface: string;
  /** Only ever set when the launch originated from that SP's full-profile view. */
  profileViewEngagementId?: string;
  /**
   * True only in a verified client-linked context — "My Clients" matchmaking,
   * or a Navigator session explicitly tied to a client (spec §3.3). Merely
   * coming from Navigator without a proven client association does not
   * count and must still prompt.
   */
  isClientLinked?: boolean;
};

export type UseSpMessageLaunchResult = {
  /** Runs the eligibility gate, then navigates directly, infers intent, or opens the modal. */
  launch: () => Promise<void>;
  isChecking: boolean;
  /** Render this alongside the CTA — it stays closed until the gate needs it. */
  modal: ReactElement;
};

/**
 * Single shared gate every SP-targeting CTA calls before navigating to
 * Messages (spec §3.3, ticket 07). Centralizes the eligibility read and the
 * direct-navigate / infer / modal branching so no CTA re-implements it.
 *
 * Gated actors: FA/subdelegate → SP first contact (spec §1, 'fa' catalog)
 * and, since feature 102, SP → SP first contact ('sp' subject catalog). Any
 * other actor (care seeker, guest) skips the eligibility read entirely and
 * navigates exactly as before. Every failure path fails open to a plain
 * navigation — the chat surface's own fallback re-prompts there.
 */
export function useSpMessageLaunch({
  serviceProviderId,
  providerName,
  sourceSurface,
  profileViewEngagementId,
  isClientLinked = false,
}: UseSpMessageLaunchParams): UseSpMessageLaunchResult {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectiveRole } = useWorkspace();
  const [isChecking, setIsChecking] = useState(false);
  const [modalRecipientUserId, setModalRecipientUserId] = useState<string | null>(null);

  const actorRole = effectiveRole ?? user?.role;
  const isFaActor = actorRole === 'FINANCIAL_ADVISOR';
  // Feature 102: SP actors are gated too, with the SP subject catalog.
  const isSpActor = actorRole === 'SERVICE_PROVIDER';

  const messagesUrl = `/messages?serviceProviderId=${serviceProviderId}${
    profileViewEngagementId
      ? `&profileViewEngagementId=${encodeURIComponent(profileViewEngagementId)}`
      : ''
  }`;

  const launch = useCallback(async () => {
    if (!isFaActor && !isSpActor) {
      navigate(messagesUrl);
      return;
    }

    setIsChecking(true);
    try {
      const { recipientUserId, requiresIntent } = await getFirstContactEligibility(serviceProviderId);

      if (!requiresIntent) {
        navigate(messagesUrl);
        return;
      }

      // Client-linked inference is an FA-only concept — an SP actor always
      // gets the subject modal on a genuine first contact.
      if (isFaActor && isClientLinked) {
        writePendingIntent({
          activeWorkspaceOwnerId: activeWorkspaceStore.get() ?? user?.id ?? '',
          recipientUserId,
          serviceProviderId,
          messageType: 'SEEKING_CARE_FOR_SPECIFIC_CLIENT',
          ...(profileViewEngagementId ? { profileViewEngagementId } : {}),
          sourceSurface,
        });
        navigate(messagesUrl);
        return;
      }

      setModalRecipientUserId(recipientUserId);
    } catch (error) {
      // Fail closed to a plain navigation — never blocks the user, never
      // guesses at intent (spec §3.5). The chat surface's own fallback
      // (ticket 10) takes over from there.
      console.error('Failed to check first-contact eligibility:', error);
      navigate(messagesUrl);
    } finally {
      setIsChecking(false);
    }
  }, [isFaActor, isSpActor, serviceProviderId, isClientLinked, messagesUrl, navigate, sourceSurface, profileViewEngagementId, user?.id]);

  const modal = (
    <PreChatIntentModal
      open={modalRecipientUserId !== null}
      catalog={isSpActor ? 'sp' : 'fa'}
      onOpenChange={(open) => {
        if (!open) setModalRecipientUserId(null);
      }}
      providerName={providerName}
      activeWorkspaceOwnerId={activeWorkspaceStore.get() ?? user?.id ?? ''}
      serviceProviderId={serviceProviderId}
      recipientUserId={modalRecipientUserId ?? ''}
      profileViewEngagementId={profileViewEngagementId}
      sourceSurface={sourceSurface}
      onContinue={() => {
        setModalRecipientUserId(null);
        navigate(messagesUrl);
      }}
    />
  );

  return { launch, isChecking, modal };
}
